import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================================================
// Manage Settings Store
// Persistent project-wide settings driven by the Manage tab dialogs:
// Project Units, Object Styles, Starting View, Macro Manager and
// material cost overrides.
// ============================================================================

export type UnitSymbol =
    | 'mm' | 'cm' | 'm'          // metric length
    | 'ft' | 'in'                // imperial length
    | 'm2' | 'ft2'               // area
    | 'm3' | 'ft3'               // volume
    | 'deg' | 'grad' | 'rad';    // angle

export interface UnitSettings {
    length: UnitSymbol;
    area: UnitSymbol;
    volume: UnitSymbol;
    angle: UnitSymbol;
}

export interface ObjectStyle {
    /** Line weight in mm (BIM-style 0.25 - 2.11) */
    lineWeight: number;
    lineColor: string;
    linePattern: 'Solid' | 'Dash' | 'Dot' | 'Dash Dot' | 'Long Dash';
    /** BIM-like default material assigned to the whole category by name. */
    materialName: string;
}

export interface MacroDefinition {
    id: string;
    name: string;
    description: string;
    /** Ordered command ids executed via useCommandManager.executeCommand */
    commandIds: string[];
}

interface ManageSettingsState {
    units: UnitSettings;
    objectStyles: Record<string, ObjectStyle>;
    startingViewId: string | null;
    macros: MacroDefinition[];
    materialOverrides: Record<string, { unitCost: number; wasteFactor: number }>;

    setUnit: (kind: keyof UnitSettings, unit: UnitSymbol) => void;
    resetUnits: () => void;

    updateObjectStyle: (category: string, patch: Partial<ObjectStyle>) => void;
    resetObjectStyle: (category: string) => void;

    setStartingView: (viewId: string | null) => void;

    addMacro: (macro: MacroDefinition) => void;
    removeMacro: (id: string) => void;
    updateMacro: (id: string, patch: Partial<Omit<MacroDefinition, 'id'>>) => void;

    setMaterialOverride: (materialId: string, patch: { unitCost?: number; wasteFactor?: number }) => void;
    clearMaterialOverride: (materialId: string) => void;
}

const defaultUnits: UnitSettings = {
    length: 'm',
    area: 'm2',
    volume: 'm3',
    angle: 'deg',
};

const defaultObjectStyle: ObjectStyle = {
    lineWeight: 1,
    lineColor: '#BFBFBF',
    linePattern: 'Solid',
    materialName: '',
};

const defaultCategories = [
    'Walls',
    'Floors',
    'Roofs',
    'Ceilings',
    'Columns',
    'Beams',
    'Doors',
    'Windows',
    'Furniture',
    'Stairs',
    'Ducts',
    'Pipes',
    'Cable Trays',
    'Conduits',
    'Lighting Fixtures',
    // Precast tab categories
    'Precast Walls',
    'Precast Floors',
    'Precast Columns',
    'Precast Beams',
    'Precast Mounting Parts',
    'Precast Reinforcement',
];

const buildDefaultObjectStyles = (): Record<string, ObjectStyle> => {
    const styles: Record<string, ObjectStyle> = {};
    defaultCategories.forEach((category) => {
        styles[category] = { ...defaultObjectStyle };
    });
    return styles;
};

export const DEFAULT_OBJECT_STYLE_CATEGORIES = defaultCategories;
export const DEFAULT_OBJECT_STYLE = defaultObjectStyle;

export const useManageSettingsStore = create<ManageSettingsState>()(
    persist(
        (set) => ({
            units: { ...defaultUnits },
            objectStyles: buildDefaultObjectStyles(),
            startingViewId: null,
            macros: [],
            materialOverrides: {},

            setUnit: (kind, unit) =>
                set((state) => ({ units: { ...state.units, [kind]: unit } })),

            resetUnits: () => set({ units: { ...defaultUnits } }),

            updateObjectStyle: (category, patch) =>
                set((state) => ({
                    objectStyles: {
                        ...state.objectStyles,
                        [category]: {
                            ...(state.objectStyles[category] ?? defaultObjectStyle),
                            ...patch,
                        },
                    },
                })),

            resetObjectStyle: (category) =>
                set((state) => ({
                    objectStyles: {
                        ...state.objectStyles,
                        [category]: { ...defaultObjectStyle },
                    },
                })),

            setStartingView: (viewId) => set({ startingViewId: viewId }),

            addMacro: (macro) =>
                set((state) => ({ macros: [...state.macros, macro] })),

            removeMacro: (id) =>
                set((state) => ({ macros: state.macros.filter((m) => m.id !== id) })),

            updateMacro: (id, patch) =>
                set((state) => ({
                    macros: state.macros.map((m) => (m.id === id ? { ...m, ...patch } : m)),
                })),

            setMaterialOverride: (materialId, patch) =>
                set((state) => {
                    const current = state.materialOverrides[materialId] ?? { unitCost: 0, wasteFactor: 0.1 };
                    return {
                        materialOverrides: {
                            ...state.materialOverrides,
                            [materialId]: { ...current, ...patch },
                        },
                    };
                }),

            clearMaterialOverride: (materialId) =>
                set((state) => {
                    const overrides = { ...state.materialOverrides };
                    delete overrides[materialId];
                    return { materialOverrides: overrides };
                }),
        }),
        {
            name: 'yap-manage-settings',
        }
    )
);
